import { useLoadScript } from "@react-google-maps/api";
import { useEffect, useRef } from "react";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import {
  FlightTerminalLeg,
  findProviderTerminalPoi,
  isAirportPoi,
  poiCoords,
  poiIsAtFlightTerminal,
  poiNamesAirport,
  poiStaysAtAirport,
} from "@/helpers/bookingHelpers";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "@/lib/googleMaps";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useListProviderPoisQuery } from "@/redux/apis/providerPoi/providerPoiApi";
import { useAppSelector } from "@/redux/hooks";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { findGoogleTerminalPoi } from "../terminalPoi";
import { useAddressSearchCountries } from "./useAddressSearchCountries";

// Module-level so the empty-POI fallback stays referentially stable across renders — the effect below
// has the POI list in its dependencies.
const NO_POIS: PointsOfInterest[] = [];

/**
 * TERMINAL SWAP — move a booking location to the terminal the flight actually uses, once the lookup
 * says which. The ARRIVAL terminal for a pickup (DEV-1594); the DEPARTURE terminal for a drop-off
 * (DEV-1487).
 *
 * A client booking an airport stop picks "Sydney Airport" — or last month's terminal — and the terminal
 * is what the driver needs. It is knowable: the same lookup that times the car already carries the
 * leg's `terminal`. So rather than asking the client to know it, the sheet reads it and moves the
 * location, and the Booking Summary discloses the move.
 *
 * ONE hook parameterised by leg rather than two copies. The rules below are subtle — a miss must be
 * remembered but an in-flight lookup must not, provider rows outrank Google, the Google fallback is a
 * billed request — and two implementations of them is how the pickup swap and the drop-off swap end up
 * disagreeing about when a flight "names a terminal".
 *
 * ## What it will not do
 *
 * Nothing runs without a terminal on the leg, and nothing runs when the location already names that
 * terminal at that airport — which is also what makes it idempotent, since the POI it swaps in
 * satisfies exactly that check. Nor does it ever move a location to an airport the location's own
 * address does not name, whatever leg the lookup answered with (DCS-403) — nor onto a row that is
 * nowhere near the location it replaces, however the row is labelled (DCS-414). Whether the flight is
 * still going ahead is the CALLER's gate: an arrival uses `flightWillArrive`, and a departure asks the
 * same question of its own leg.
 *
 * ## One attempt per location
 *
 * `attempted` holds the flight + terminal the last attempt was made for, cleared whenever the location
 * itself moves. A successful swap needs no guard (the match check closes it), but a MISS does: with no
 * provider row and no Google hit there is nothing to change, so without this the effect would re-ask on
 * every render the flight object is replaced on. Clearing it on a location change is what lets the swap
 * run again after the client edits the address by hand and re-enters the flight.
 */
export const useTerminalSwap = ({
  enabled,
  legResolved,
  flightNumber,
  location,
  leg,
  onSwap,
}: {
  /**
   * The caller's standing gate — view-only, frozen, a lookup error. Deliberately NOT narrowed by
   * whether the flight has resolved yet: it also skips the provider-POI query, and serialising that
   * fetch behind the flight fetch delays every swap by a round trip for no gain. Provider rows are
   * consulted first, so they have to be in hand by the time the flight lands.
   */
  enabled: boolean;
  /** The flight has resolved AND is going ahead — gates the swap itself, never the POI subscription. */
  legResolved: boolean;
  /** The captured flight number, and the reason this location is being moved. */
  flightNumber: string | null;
  /** The POI the swap would replace — the pickup, or the drop-off. */
  location: PointsOfInterest | null;
  /** The resolved leg's airport + terminal. */
  leg: FlightTerminalLeg;
  /** Commit the swap. The caller owns which reducer that is, and what it records as the source. */
  onSwap: (poi: PointsOfInterest) => void;
}) => {
  const operatorId = useAppSelector(operatorIdSelector);
  const { isGuest } = useGuestBooking();
  const readsProviderPois = !isGuest && !!operatorId;
  // The terminal lookup follows the address search's country rule (DEV-1606), so a WLG flight resolves
  // for the same people who can type a Wellington address — on BOTH legs. See `findGoogleTerminalPoi`
  // for why scoping the departure end is safe rather than merely consistent.
  const countries = useAddressSearchCountries();
  // Same argument the address search subscribes with, so this shares that cache entry rather than
  // fetching the provider's POIs a second time.
  const {
    data: providerPois = NO_POIS,
    isLoading: poisLoading,
    isError: poisError,
    isUninitialized: poisUninitialized,
  } = useListProviderPoisQuery(undefined, { skip: !enabled || !readsProviderPois });
  // NOT KNOWN vs known-empty. `isLoading` alone is false for an errored or not-yet-subscribed query and
  // `data` is then undefined, which reads as "the operator has no row for this terminal" and bills the
  // Google fallback over a row that may well exist.
  const providerPoisUnknown = readsProviderPois && (poisLoading || poisError || poisUninitialized);
  const { isLoaded: googleLoaded } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);

  // The leg is rebuilt on every render by the caller, so the effect keys on its fields rather than its
  // identity — otherwise it re-runs (and re-reads `attempted`) on every render of the sheet.
  const { city, iata, name, terminal } = leg;

  const attempted = useRef<string | null>(null);
  useEffect(() => {
    attempted.current = null;
  }, [location]);

  // `onSwap` is a fresh closure each render; holding it in a ref keeps it out of the dependency list so
  // a caller that does not memoise cannot make this effect re-run — and re-ask Google — every render.
  const onSwapRef = useRef(onSwap);
  onSwapRef.current = onSwap;

  useEffect(() => {
    // Provider POIs rank ahead of Google, so an attempt made before they land could reach for Google
    // over a row that was about to arrive — and `attempted` would then hold that miss.
    if (!enabled || !legResolved || providerPoisUnknown || !flightNumber) return;
    // An AIRPORT location only. A flight number is not proof of one: `viewDraft` hydrates the stored
    // numbers whatever the addresses are, so opening a booking with a hotel pickup and a flight on it
    // would otherwise move that pickup to a terminal — the client would find it relocated by an edit
    // they came to make somewhere else.
    if (!isAirportPoi(location)) return;
    if (!terminal || !iata) return;
    const target: FlightTerminalLeg = { city, iata, name, terminal };
    if (poiIsAtFlightTerminal(location, target)) return;
    // An unconstrained lookup can answer with the wrong leg of a multi-leg flight, so the location has
    // the last word on which airport it is at. Ahead of `attempted`: this is a standing property of the
    // pair rather than an attempt that could succeed on a later render, and recording it would suppress
    // the swap that a corrected leg should still get.
    if (!poiNamesAirport(location, target)) return;

    const key = `${flightNumber}|${iata}|${terminal}`;
    if (attempted.current === key) return;
    attempted.current = key;

    // The last word on the POI actually chosen, as `poiNamesAirport` is on the leg: a row may satisfy
    // every check above and still sit at another airport, because the Google fallback relabels what it
    // finds with the leg's airport (DCS-414). Committing is the one place both sources pass through.
    //
    // Distance is the only test worth making HERE. The candidate's own IATA code is the other half of the
    // guard, but neither source can still be contradicted by it this late — a provider row matched the
    // leg's code to be found at all, and a Google row has just been relabelled with it — so each source is
    // checked on its code where the code is still its own.
    const commit = (poi: PointsOfInterest) => {
      if (poiStaysAtAirport(location, poi)) onSwapRef.current(poi);
    };

    const providerHit = findProviderTerminalPoi(target, providerPois);
    if (providerHit) {
      commit(providerHit);
      return;
    }
    // No provider row for this terminal — fall through to Google, but only once its single loader is
    // ready. Re-asked rather than dropped: this attempt is released so the effect runs again on the
    // render that reports the loader up.
    if (!googleLoaded) {
      attempted.current = null;
      return;
    }
    let cancelled = false;
    let settled = false;
    findGoogleTerminalPoi(target, countries, poiCoords(location)).then((poi) => {
      settled = true;
      if (!cancelled && poi) commit(poi);
    });
    return () => {
      cancelled = true;
      // A lookup cut short by a re-run has not answered yet, so the attempt is released and asked again.
      // Holding the key here loses the swap entirely: the flight query refetches whenever a subscriber
      // mounts onto a stale entry (opening the Date & Time step), which re-runs this effect straight
      // through an in-flight Places call.
      if (!settled) attempted.current = null;
    };
  }, [
    enabled,
    legResolved,
    providerPoisUnknown,
    flightNumber,
    location,
    city,
    iata,
    name,
    terminal,
    providerPois,
    googleLoaded,
    countries,
  ]);
};
